import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomySize,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/Link/Link.module.scss. Link has no colour variants:
// the variant axis is its content (text, icon slots, icon-only), and every
// state block is shared by all of them.

const parts: readonly AnatomyPart[] = [
    {
        id: "background",
        label: "Container background",
        element: "background-color on .link, painted only while focused",
    },
    {
        id: "border",
        label: "Focus line",
        element:
            "inset box-shadow on .link; no border at rest, so nothing shifts",
    },
    {
        id: "label",
        label: "Label",
        element: "color and text style on .link",
    },
    {
        id: "glyph",
        label: "Icon",
        element: "svg in .leftSlot / .rightSlot, or the bare icon-only glyph",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element: "outer box-shadow on .link",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Hover",
        selector: ':not(:disabled, [aria-disabled="true"]):hover',
    },
    {
        id: "pressed",
        label: "Pressed",
        selector: ':not(:disabled, [aria-disabled="true"]):active',
    },
    {
        id: "focus",
        label: "Focus",
        selector: ':not(:disabled, [aria-disabled="true"]):focus-visible',
    },
    {
        id: "disabled",
        label: "Disabled",
        selector: ':is(:disabled, [aria-disabled="true"])',
    },
];

const text = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
});

const glyphBox = (size: string): readonly AnatomyBinding[] => [
    { partId: "glyph", property: "width", literal: size },
    { partId: "glyph", property: "height", literal: size },
];

const sharedBindings: readonly AnatomyBinding[] = [
    {
        partId: "background",
        property: "background-color",
        literal: "transparent",
        note: "background: none in the element reset; only focus paints a fill.",
    },
    { partId: "background", property: "gap", token: "--space-4" },
    { partId: "border", property: "border-radius", token: "--radius-m" },
    text("--fg-primary"),
    ...glyphBox("16px"),
    {
        partId: "glyph",
        property: "color",
        literal: "currentColor",
        note: "component.recipe-icon-color: icons follow the label colour in every state.",
    },
];

const stateBindings: AnatomyVariant["stateBindings"] = {
    hover: [text("--fg-secondary")],
    pressed: [
        {
            ...text("--fg-secondary"),
            note: "Snaps in with transition-duration: 0ms and fades out on release.",
        },
    ],
    focus: [
        {
            partId: "background",
            property: "background-color",
            token: "--bg-primary",
        },
        text("--fg-primary"),
        {
            partId: "border",
            property: "box-shadow (inset width)",
            token: "--border-width",
        },
        {
            partId: "border",
            property: "box-shadow (inset color)",
            token: "--border-accent-focus",
        },
        {
            partId: "focusRing",
            property: "box-shadow (spread)",
            token: "--border-width-thick",
        },
        {
            partId: "focusRing",
            property: "box-shadow (color)",
            token: "--focus-ring-color",
        },
    ],
    disabled: [text("--fg-disabled")],
};

const variants: readonly AnatomyVariant[] = [
    {
        id: "text",
        label: "Text",
        selector: "",
        restingBindings: [],
        stateBindings,
    },
    {
        id: "leftIcon",
        label: "Left icon",
        selector: ":has(> .leftSlot)",
        restingBindings: [],
        stateBindings,
    },
    {
        id: "rightIcon",
        label: "Right icon",
        selector: ":has(> .rightSlot)",
        restingBindings: [],
        stateBindings,
    },
    {
        id: "bothIcons",
        label: "Both icons",
        selector: ":has(> .leftSlot):has(> .rightSlot)",
        restingBindings: [],
        stateBindings,
    },
    {
        id: "iconOnly",
        label: "Icon only",
        selector: ".iconOnly",
        restingBindings: glyphBox("12px"),
        stateBindings,
    },
];

// No height is declared: each size is the line box of its text style.
const sizes: readonly AnatomySize[] = [
    {
        id: "sm",
        label: "sm",
        selector: ".sm",
        bindings: anatomyTextStyle({
            scale: "s",
            weight: "500",
            mixin: "text-s-14-medium",
            note: "Figma's 24px Link; the 20px line height sets its rendered height.",
        }),
    },
    {
        id: "xs",
        label: "xs",
        selector: ".xs",
        bindings: anatomyTextStyle({
            scale: "xs",
            weight: "500",
            mixin: "text-xs-12-medium",
            note: "Figma's 20px Link; the 18px line height sets its rendered height.",
        }),
    },
];

export const linkAnatomy: ComponentAnatomySpec = {
    label: "Link",
    schematic: "link",
    rootSelector: ".link",
    variantAxisLabel: "Content",
    parts,
    states,
    sharedBindings,
    variants,
    sizes,
};
